import * as annotations from './annotations';
import { canOpenMpr, closeMip, isMipOpen, isMprOpen, openMip, toggleMpr } from './mpr';
import {
  areToolsLocked, fuseActive, isObliqueOpen, isVolume3dOpen, onToolsLockChange, openScreenDialog, toggleOblique, toggleToolsLock,
  toggleVolume3d,
} from './display';
import * as cine from './ui/cine';
import { openDownloadDialog, openHelpDialog, openMetadataDialog, snapshotViewport } from './ui/dialogs';
import type { CsViewport } from './globals';
import { isOverlayVisible, setOverlayVisible, tagReaderFor } from './overlay';
import { extraHeaderWindows } from './display/voilut';
import type { ViewerShell } from './shell';
import * as sync from './sync';
import { currentLeftTool, isToolAvailable, setLeftTool, TOOL_IDS, type ToolId } from './tools';
import type { MenuItem } from './ui/contextmenu';
import type { Viewport } from './viewport';
import { isReferenceImageId, PRESETS_UNSUPPORTED } from './display/reference';

/**
 * One command registry for the toolbar, the context menu and the keyboard (spec sections 2, 1.4, 8).
 * Every user action goes through `runCommand(id)`, so the three entry points behave identically.
 */

export const ALIGNMENTS = ['Left', 'Right', 'Top', 'Bottom', 'TopLeft', 'BottomLeft', 'TopRight', 'BottomRight'] as const;
export type Alignment = (typeof ALIGNMENTS)[number];

/** Features that exist in IMEXHS but not yet here: they show a toast instead of faking behaviour. */
export const UNAVAILABLE_COMMANDS = ['hanging-protocol', 'ai-module'] as const;
type UnavailableCommand = (typeof UNAVAILABLE_COMMANDS)[number];

/**
 * Workspace commands served by ris (src/ris). Their handlers are registered only when the viewer runs
 * against ris; without one (standalone bench) they show the "not available" toast like the others.
 */
export const RIS_COMMANDS = ['secondary-capture', 'download-dicom', 'attach-pdf', 'settings', 'reset-config', 'key-images', 'study-report', 'share-study'] as const;
export type RisCommand = (typeof RIS_COMMANDS)[number];

export type CommandId =
  | `tool:${ToolId}`
  | `preset:${number}`
  | `layout:${number}x${number}`
  | `align:${Alignment}`
  | 'invert' | 'rotate' | 'flip-h' | 'flip-v' | 'reset' | 'fit' | 'full-screen'
  | 'slice-next' | 'slice-prev' | 'series-next' | 'series-prev'
  | 'toggle-overlay' | 'toggle-link' | 'toggle-manual-link' | 'toggle-reference-lines'
  | 'clear-all' | 'clear-slice' | 'cine-toggle' | 'cine-bar-toggle'
  | 'copy-image' | 'copy-grid' | 'download-dialog' | 'metadata-dialog' | 'help-dialog' | 'reload'
  | 'mpr' | 'mip'
  | 'mpr-oblique' | 'volume-3d' | 'multi-screen' | 'lock-tools' | `fuse:${string}`
  | `keyimage:${string}`
  | RisCommand | UnavailableCommand;

export const UNAVAILABLE_MESSAGE = 'Función no disponible todavía.';
/** lib-permission-denied-modal body. */
export const PERMISSION_DENIED = 'No dispone de los permisos necesarios para realizar esta acción.';
const COPY_OK = 'La imagen actual ha sido copiada exitosamente.';
const COPY_FAILED = 'No se pudo copiar la imagen, por favor intenta de nuevo.';
const SNACKBAR_MS = 3000;
const ROTATION_STEP = 90;
const FULL_TURN = 360;

/** Config key → Spanish menu name (IMEXHS i18n switch on the preset key, spec section 4). */
const PRESET_NAMES: Record<string, string> = {
  Abdomen: 'Abdomen',
  Lung: 'Pulmón',
  Brain: 'Cerebro',
  Bone: 'Hueso',
  'Head/Neck': 'Cabeza/Cuello',
  Head: 'Cabeza',
  Neck: 'Cuello',
  Ear: 'Oído',
  Ictus: 'Ictus',
  Mediastinum: 'Mediastino',
};
export const DEFAULT_PRESET_LABEL = '(1) Predeterminado';

/** IMEXHS `viewer_config.js` gates the toolbar reads (spec section 7); defaults are that deployment's values. */
export interface ViewerFlags {
  tools3d: boolean;
  keyImagesModule: boolean;
  segmentationModule: boolean;
  hangingEnabled: boolean;
  stowUrl: string;
  uploadEnabled: boolean;
  downloadDicomEnabled: boolean;
  multiScreenOption: boolean;
  settingsEnabled: boolean;
  flipToolsInTopMenu: boolean;
  advancedAnnotationsEnabled: boolean;
  obliqueMprEnabled: boolean;
  noAxialMpr: boolean;
  reportsModule: boolean;
}

const FLAG_DEFAULTS: ViewerFlags = {
  tools3d: true,
  keyImagesModule: true,
  segmentationModule: false,
  hangingEnabled: false,
  stowUrl: '/second',
  uploadEnabled: true,
  downloadDicomEnabled: true,
  multiScreenOption: true,
  settingsEnabled: true,
  flipToolsInTopMenu: false,
  advancedAnnotationsEnabled: true,
  obliqueMprEnabled: true,
  noAxialMpr: true,
  reportsModule: false,
};

/** Modalities IMEXHS offers MPR / MIP for (volumetric stacks). */
const MPR_MODALITIES = new Set(['CT', 'MR', 'PT', 'NM']);

/** Per-user overrides of the config gates (ris preferences, "Configuración" → Visualización). */
let flagOverrides: Partial<ViewerFlags> = {};

export function setFlagOverrides(overrides: Partial<ViewerFlags>): void {
  flagOverrides = { ...overrides };
}

/** Reads the IMEXHS config gates from `window.VIEWER_CONFIG`, falling back to the deployment defaults. */
export function viewerFlags(shell: ViewerShell): ViewerFlags {
  const flags: ViewerFlags = { ...FLAG_DEFAULTS };
  for (const key of Object.keys(FLAG_DEFAULTS) as Array<keyof ViewerFlags>) {
    const value = shell.config[key];
    if (typeof value === typeof FLAG_DEFAULTS[key]) Object.assign(flags, { [key]: value });
  }
  return { ...flags, ...flagOverrides };
}

// --- Commands served by other modules (src/ris) ------------------------------------------------

type CommandHandler = (arg: string | undefined) => void;
const handlers = new Map<string, CommandHandler>();
let commandGate: (id: CommandId) => boolean = () => true;

/** Registers the handler of a command kind (`key-images`, or a prefix like `keyimage` for `keyimage:<id>`). */
export function registerCommand(kind: string, handler: CommandHandler): void {
  handlers.set(kind, handler);
}

/** True when a module serves the command (ris commands without ris are not served). */
export function isCommandServed(id: CommandId): boolean {
  return handlers.has(id.split(':')[0]);
}

/** Decides which commands the session may run; menus hide the others (ris session permissions). */
export function setCommandGate(gate: (id: CommandId) => boolean): void {
  commandGate = gate;
}

/**
 * Narrows the current gate: a command now also needs `allow` (the referrer layout, src/referrer). Runs
 * after ris installed its permission gate; menus hide what it refuses and runCommand refuses it too.
 */
export function restrictCommands(allow: (id: CommandId) => boolean): void {
  const base = commandGate;
  commandGate = (id) => allow(id) && base(id);
}

export function isCommandAllowed(id: CommandId): boolean {
  return commandGate(id);
}

export interface CommandState {
  linked: boolean;
  manualLinked: boolean;
  referenceLines: boolean;
  overlayVisible: boolean;
  fullScreen: boolean;
  cinePlaying: boolean;
  cineBarVisible: boolean;
  toolsLocked: boolean;
}

let shellRef: ViewerShell | null = null;
const stateListeners = new Set<(state: CommandState) => void>();

function requireShell(): ViewerShell {
  if (!shellRef) throw new Error('commands.install() must run first');
  return shellRef;
}

/** Current state of every toggle the toolbar and context menu show. */
export function commandState(): CommandState {
  const shell = shellRef;
  return {
    linked: sync.isLinked(),
    manualLinked: sync.isManualLinked(),
    referenceLines: sync.areReferenceLinesOn(),
    overlayVisible: isOverlayVisible(),
    fullScreen: shell ? shell.fullScreenIndex() !== undefined : false,
    cinePlaying: cine.isCinePlaying(),
    cineBarVisible: cine.isCineBarVisible(),
    toolsLocked: areToolsLocked(),
  };
}

/** Called after any toggle changes (link, overlay, cine, full screen…). */
export function onCommandStateChange(listener: (state: CommandState) => void): () => void {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

function notifyState(): void {
  const state = commandState();
  stateListeners.forEach((listener) => listener(state));
}

export function toolCommand(id: ToolId): CommandId {
  return `tool:${id}`;
}

// --- Active viewport helpers ------------------------------------------------------------------

function activeWithImage(): Viewport | undefined {
  const vp = requireShell().activeViewport();
  return vp?.currentImage() ? vp : undefined;
}

function editViewport(change: (viewport: CsViewport, vp: Viewport) => void): void {
  const vp = activeWithImage();
  if (!vp) return;
  const viewport = window.cornerstone.getViewport(vp.element);
  if (!viewport) return;
  change(viewport, vp);
  window.cornerstone.setViewport(vp.element, viewport);
}

function activeModality(shell: ViewerShell): string {
  return shell.activeViewport()?.currentStack?.series.modality ?? shell.study().series[0]?.modality ?? '';
}

// --- W/L presets --------------------------------------------------------------------------------

export interface WindowPreset {
  key: string;
  label: string;
  width: number;
  center: number;
}

/**
 * `display.default.presetWindows` then `display[modality].presetWindows` for the active viewport (spec
 * section 4), then the image's own extra windows: Window Center / Width are multi-valued, the first is
 * "(1) Predeterminado" and the others (often a soft-tissue and a bone window, named by Window Center &
 * Width Explanation 0028,1055) are listed last so the configured presets keep their keys.
 */
export function presetsFor(shell: ViewerShell, modality = activeModality(shell)): WindowPreset[] {
  const { display } = shell.config;
  const sources = [display.default?.presetWindows ?? {}];
  if (modality && modality !== 'default' && display[modality]) sources.push(display[modality].presetWindows ?? {});
  const configured = sources.flatMap((windows) =>
    Object.entries(windows).map(([key, [width, center]]) => ({ key, label: PRESET_NAMES[key] ?? key, width, center })),
  );
  return [...configured, ...headerPresets(shell.activeViewport())];
}

/** The second and later windows of the image on screen ("Ventana DICOM 2", or its explanation). */
export function headerPresets(viewport: Viewport | undefined): WindowPreset[] {
  const reader = viewport ? tagReaderFor(viewport) : undefined;
  if (!reader) return [];
  return extraHeaderWindows(reader.numbers('00281050'), reader.numbers('00281051'), reader.text('00281055').split('\\'));
}


/** Menu entries "(1) Predeterminado" + presets, shared by the Ventana dropdown and the context menu. */
export function presetMenuEntries(shell: ViewerShell): MenuItem[] {
  return [
    { id: 'preset-0', label: DEFAULT_PRESET_LABEL, command: 'preset:0' },
    ...presetsFor(shell).map((preset, index): MenuItem => ({
      id: `preset-${index + 1}`,
      label: preset.label,
      title: `W: ${preset.width} L: ${preset.center}`,
      command: `preset:${index + 1}`,
    })),
  ];
}

function firstNumber(value: number | number[] | undefined): number {
  return Array.isArray(value) ? Number(value[0]) : Number(value);
}

/** Preset k: 0 restores the image's own DICOM window, k ≥ 1 applies preset k−1. */
function applyPreset(index: number): void {
  const shell = requireShell();
  const vp = activeWithImage();
  const image = vp?.currentImage();
  if (!vp || !image) return;
  if (isReferenceImageId(image.imageId)) {
    shell.toast(PRESETS_UNSUPPORTED, 'warning');
    return;
  }
  let width: number;
  let center: number;
  if (index === 0) {
    width = firstNumber(image.windowWidth);
    center = firstNumber(image.windowCenter);
    if (!Number.isFinite(width) || !Number.isFinite(center) || width <= 0) {
      const fallback = window.cornerstone.getDefaultViewportForImage(vp.element, image).voi;
      width = fallback.windowWidth;
      center = fallback.windowCenter;
    }
  } else {
    const preset = presetsFor(shell)[index - 1];
    if (!preset) return;
    width = preset.width;
    center = preset.center;
  }
  editViewport((viewport) => {
    viewport.voi = { windowWidth: width, windowCenter: center };
  });
}

// --- Geometry: rotate, flip, align ------------------------------------------------------------

interface Point {
  x: number;
  y: number;
}

function canvasBounds(element: HTMLElement, rows: number, columns: number): { minX: number; minY: number; maxX: number; maxY: number } {
  const corners = [{ x: 0, y: 0 }, { x: columns, y: 0 }, { x: 0, y: rows }, { x: columns, y: rows }]
    .map((p) => window.cornerstone.pixelToCanvas(element, p));
  return {
    minX: Math.min(...corners.map((p) => p.x)),
    minY: Math.min(...corners.map((p) => p.y)),
    maxX: Math.max(...corners.map((p) => p.x)),
    maxY: Math.max(...corners.map((p) => p.y)),
  };
}

/**
 * Snaps the image to a viewport edge or corner by changing only the translation. The canvas shift
 * a unit of translation produces is probed, so rotation, flips and pixel aspect are all honoured.
 */
function align(alignment: Alignment): void {
  const vp = activeWithImage();
  const image = vp?.currentImage();
  if (!vp || !image) return;
  const enabled = window.cornerstone.getEnabledElement(vp.element);
  const viewport = enabled.viewport;
  if (!viewport) return;
  const { rows, columns } = image;
  const origin = { ...viewport.translation };
  const bounds = canvasBounds(vp.element, rows, columns);
  const probe = (dx: number, dy: number): Point => {
    viewport.translation = { x: origin.x + dx, y: origin.y + dy };
    const moved = canvasBounds(vp.element, rows, columns);
    return { x: moved.minX - bounds.minX, y: moved.minY - bounds.minY };
  };
  const ex = probe(1, 0);
  const ey = probe(0, 1);
  viewport.translation = origin;
  const width = enabled.canvas.width;
  const height = enabled.canvas.height;
  // Only the named edges move: "Left" keeps the vertical position, "TopLeft" moves both.
  const shiftX = alignment.includes('Left') ? -bounds.minX : alignment.includes('Right') ? width - bounds.maxX : 0;
  const shiftY = alignment.includes('Top') ? -bounds.minY : alignment.includes('Bottom') ? height - bounds.maxY : 0;
  // Solve [ex ey] · t = shift for the translation change t.
  const det = ex.x * ey.y - ey.x * ex.y;
  if (Math.abs(det) < 1e-9) return;
  const tx = (shiftX * ey.y - ey.x * shiftY) / det;
  const ty = (ex.x * shiftY - shiftX * ex.y) / det;
  editViewport((next) => {
    next.translation = { x: origin.x + tx, y: origin.y + ty };
  });
}

// --- Stack and series navigation ------------------------------------------------------------

function changeSlice(delta: number): void {
  const vp = activeWithImage();
  const stack = vp?.currentStack;
  const index = vp?.currentIndex();
  if (!vp || !stack || index === undefined) return;
  const next = Math.min(Math.max(index + delta, 0), stack.imageIds.length - 1);
  if (next !== index) window.cornerstoneTools.scrollToIndex(vp.element, next);
}

function changeSeries(delta: number): void {
  const shell = requireShell();
  const vp = shell.activeViewport();
  const all = shell.studies().flatMap((study) => study.series);
  if (!vp || all.length === 0) return;
  const current = all.findIndex((s) => s.seriesInstanceUID === vp.currentStack?.series.seriesInstanceUID);
  const next = current < 0 ? 0 : current + delta;
  if (next < 0 || next >= all.length || next === current) return;
  void shell.showSeriesIn(shell.activeIndex(), all[next]);
}

// --- Clipboard ------------------------------------------------------------------------------

/** Snackbar (3 s, bottom-centre) for the copy result, like IMEXHS lib-copy-status-modal. */
export function snackbar(message: string, kind: 'info' | 'error' = 'info'): void {
  const shell = requireShell();
  shell.regions.overlayLayer.querySelectorAll('.cmd-snackbar').forEach((el) => el.remove());
  const bar = document.createElement('div');
  bar.className = `cmd-snackbar cmd-snackbar-${kind}`;
  bar.setAttribute('role', 'status');
  bar.textContent = message;
  shell.regions.overlayLayer.appendChild(bar);
  setTimeout(() => bar.remove(), SNACKBAR_MS);
}

async function writeCanvasToClipboard(canvas: HTMLCanvasElement | null): Promise<void> {
  try {
    if (!canvas || !navigator.clipboard || typeof ClipboardItem === 'undefined') throw new Error('clipboard unavailable');
    // Browsers only accept PNG for image clipboard writes; IMEXHS labels it "jpg".
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('canvas export failed');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    snackbar(COPY_OK);
  } catch (error) {
    console.warn('[viewer] copy failed', error);
    snackbar(COPY_FAILED, 'error');
  }
}

/** The whole grid composed from every viewport canvas at its on-screen position (Ctrl+C). */
function snapshotGrid(shell: ViewerShell): HTMLCanvasElement | null {
  const gridRect = shell.regions.grid.getBoundingClientRect();
  const cells = shell.viewports().filter((vp) => vp.currentImage() && vp.container.offsetParent !== null);
  if (cells.length === 0 || gridRect.width === 0) return null;
  const out = document.createElement('canvas');
  out.width = Math.round(gridRect.width);
  out.height = Math.round(gridRect.height);
  const context = out.getContext('2d');
  if (!context) return null;
  context.fillStyle = '#000';
  context.fillRect(0, 0, out.width, out.height);
  for (const vp of cells) {
    const rect = vp.container.getBoundingClientRect();
    const canvas = snapshotViewport(vp);
    if (canvas) context.drawImage(canvas, rect.left - gridRect.left, rect.top - gridRect.top, rect.width, rect.height);
  }
  return out;
}

// --- Reload --------------------------------------------------------------------------------

/** "Recargar": purge every cached image and show each viewport's series again at the same slice. */
async function reloadAll(): Promise<void> {
  const shell = requireShell();
  cine.stopCine();
  const shown = shell.viewports().map((vp) => ({ series: vp.currentStack?.series, index: vp.currentIndex() }));
  window.cornerstone.imageCache.purgeCache();
  window.cornerstoneWADOImageLoader.wadouri?.dataSetCacheManager.purge();
  await Promise.all(shown.map(({ series, index }, i) => (series ? shell.showSeriesIn(i, series, index) : Promise.resolve())));
}

// --- Registry ------------------------------------------------------------------------------

function toggleFullScreen(): void {
  const shell = requireShell();
  const active = shell.activeIndex();
  shell.setFullScreen(shell.fullScreenIndex() === active ? undefined : active);
}

function activateTool(id: ToolId): void {
  const shell = requireShell();
  if (!isToolAvailable(id) || !setLeftTool(id)) shell.toast(UNAVAILABLE_MESSAGE, 'warning');
}

function runFixed(id: CommandId): boolean {
  const shell = requireShell();
  switch (id) {
    case 'invert': editViewport((v) => { v.invert = !v.invert; }); return true;
    case 'rotate': editViewport((v) => { v.rotation = (((v.rotation - ROTATION_STEP) % FULL_TURN) + FULL_TURN) % FULL_TURN; }); return true;
    case 'flip-h': editViewport((v) => { v.hflip = !v.hflip; }); return true;
    case 'flip-v': editViewport((v) => { v.vflip = !v.vflip; }); return true;
    case 'reset': { const vp = activeWithImage(); if (vp) window.cornerstone.reset(vp.element); return true; }
    case 'fit': { const vp = activeWithImage(); if (vp) window.cornerstone.fitToWindow(vp.element); return true; }
    case 'full-screen': toggleFullScreen(); return true;
    case 'slice-next': changeSlice(1); return true;
    case 'slice-prev': changeSlice(-1); return true;
    case 'series-next': shell.guard(() => changeSeries(1)); return true;
    case 'series-prev': shell.guard(() => changeSeries(-1)); return true;
    case 'toggle-overlay': setOverlayVisible(!isOverlayVisible()); return true;
    case 'toggle-link': sync.setLink(!sync.isLinked()); return true;
    case 'toggle-manual-link': sync.setManualLink(!sync.isManualLinked()); return true;
    case 'toggle-reference-lines': sync.setReferenceLines(!sync.areReferenceLinesOn()); return true;
    case 'clear-all': annotations.clearAllMeasurements(); return true;
    case 'clear-slice': { const vp = activeWithImage(); if (vp) annotations.clearCurrentSlice(vp); return true; }
    case 'cine-toggle': cine.toggleCine(); return true;
    case 'cine-bar-toggle': cine.setCineBarVisible(!cine.isCineBarVisible()); return true;
    case 'copy-image': { const vp = activeWithImage(); void writeCanvasToClipboard(vp ? snapshotViewport(vp) : null); return true; }
    case 'copy-grid': void writeCanvasToClipboard(snapshotGrid(shell)); return true;
    case 'download-dialog': { const vp = activeWithImage(); if (vp) openDownloadDialog(shell, vp); return true; }
    case 'metadata-dialog': { const vp = activeWithImage(); if (vp) void openMetadataDialog(shell, vp); return true; }
    case 'help-dialog': openHelpDialog(shell); return true;
    case 'reload': void reloadAll(); return true;
    case 'mpr': void toggleMprView(shell); return true;
    case 'mip': void toggleMipView(shell); return true;
    case 'mpr-oblique': void toggleObliqueView(shell); return true;
    case 'volume-3d': void toggleVolumeView(shell); return true;
    case 'multi-screen': openScreenDialog(shell); return true;
    case 'lock-tools': toggleToolsLock(); return true;
    default: return false;
  }
}

/** Runs one command on the active viewport (or the whole viewer), then refreshes toggle state. */
export function runCommand(id: CommandId): void {
  const shell = requireShell();
  const [kind, arg] = id.split(':') as [string, string | undefined];
  const handler = handlers.get(kind);
  if (handler) {
    if (commandGate(id)) handler(arg);
    else shell.toast(PERMISSION_DENIED, 'warning');
    notifyState();
    return;
  }
  if ((UNAVAILABLE_COMMANDS as readonly string[]).includes(id) || (RIS_COMMANDS as readonly string[]).includes(id)) {
    shell.toast(UNAVAILABLE_MESSAGE, 'warning');
    return;
  }
  // Built-in commands pass the gate too (the keyboard reaches some that no menu shows, e.g. Ctrl+C).
  if (!commandGate(id)) {
    shell.toast(PERMISSION_DENIED, 'warning');
    return;
  }
  if (arg !== undefined) {
    if (kind === 'tool' && (TOOL_IDS as readonly string[]).includes(arg)) activateTool(arg as ToolId);
    else if (kind === 'preset') applyPreset(Number(arg));
    else if (kind === 'align' && (ALIGNMENTS as readonly string[]).includes(arg)) align(arg as Alignment);
    else if (kind === 'fuse') void fuseActive(arg);
    else if (kind === 'layout') {
      const [rows, columns] = arg.split('x').map(Number);
      if (rows > 0 && columns > 0) shell.guard(() => shell.setLayout({ rows, columns }));
    }
  } else if (!runFixed(id)) {
    console.warn('[viewer] unknown command', id);
  }
  notifyState();
}

/** " MPR (legacy)": axial / coronal / sagittal 1x3 of the active series (vtk.js), or back. */
async function toggleMprView(shell: ViewerShell): Promise<void> {
  if (!isMprOpen() && !canOpenMpr(shell)) {
    shell.toast('MPR requiere más cortes.', 'warning');
    return;
  }
  await toggleMpr(shell);
}

/** " MIP": maximum / minimum / average projection of the active series, or back. */
async function toggleMipView(shell: ViewerShell): Promise<void> {
  if (isMipOpen()) {
    closeMip(shell);
    return;
  }
  if (!canOpenMpr(shell)) {
    shell.toast('MPR requiere más cortes.', 'warning');
    return;
  }
  await openMip(shell);
}

/** "MPR Obliquo (Beta)": three rotatable planes of the active series (vtk.js), or back. */
async function toggleObliqueView(shell: ViewerShell): Promise<void> {
  if (!isObliqueOpen() && !viewerFlags(shell).obliqueMprEnabled) {
    shell.toast(UNAVAILABLE_MESSAGE, 'warning');
    return;
  }
  if (!isObliqueOpen() && !canOpenMpr(shell)) {
    shell.toast('MPR requiere más cortes.', 'warning');
    return;
  }
  await toggleOblique(shell);
}

/** "3D": volume rendering of the active series next to it (1x2), or back. */
async function toggleVolumeView(shell: ViewerShell): Promise<void> {
  if (!isVolume3dOpen() && !canOpenMpr(shell)) {
    shell.toast('MPR requiere más cortes.', 'warning');
    return;
  }
  await toggleVolume3d(shell);
}

/** Whether MPR / MIP apply to the active viewport (IMEXHS gates them on `tools3d` and the modality). */
export function supportsMpr(shell: ViewerShell): boolean {
  return viewerFlags(shell).tools3d && MPR_MODALITIES.has(activeModality(shell));
}

interface ViewerDebug {
  run(id: CommandId): void;
  currentLeftTool(): ToolId;
  viewport(index?: number): CsViewport | undefined;
  state(): CommandState;
  presets(): WindowPreset[];
  activeIndex(): number;
  sliceIndex(): number | undefined;
  seriesUID(): string | undefined;
  toolAvailable(id: ToolId): boolean;
}

declare global {
  interface Window {
    __viewerDebug?: ViewerDebug;
  }
}

/** Installs the registry: keeps toggle state fresh and exposes `window.__viewerDebug` for the e2e checks. */
export function install(shell: ViewerShell): void {
  shellRef = shell;
  cine.onCineChange(notifyState);
  shell.on('active-changed', notifyState);
  shell.on('layout-changed', notifyState);
  shell.on('full-screen-changed', notifyState);
  onToolsLockChange(notifyState);
  window.__viewerDebug = {
    run: runCommand,
    currentLeftTool,
    viewport: (index) => {
      const vp = index === undefined ? shell.activeViewport() : shell.viewports()[index];
      if (!vp?.currentImage()) return undefined;
      const viewport = window.cornerstone.getViewport(vp.element);
      return viewport ? (JSON.parse(JSON.stringify(viewport)) as CsViewport) : undefined;
    },
    state: commandState,
    presets: () => presetsFor(shell),
    activeIndex: () => shell.activeIndex(),
    sliceIndex: () => shell.activeViewport()?.currentIndex(),
    seriesUID: () => shell.activeViewport()?.currentStack?.series.seriesInstanceUID,
    toolAvailable: isToolAvailable,
  };
}
